/**
 * feat-361 — every on-screen string of the "Move to Lesson…" action, its picker
 * dialog and the success strip (design/NOTES.md §2 is the copy oracle). Level words
 * come from QUIZ_TERMS so a rename cannot drift (DESIGN-QUESTION Q1).
 */
import { QUIZ_TERMS } from '../quizTerminology';
import { ApiError } from '../../../utils/api';

const L = QUIZ_TERMS.lesson;
const D = QUIZ_TERMS.drill;

/** `1 Drill` / `{n} Drills`. */
export const drillCount = (n: number): string => `${n} ${n === 1 ? D.singular : D.plural}`;

export const MOVE_DRILL_COPY = {
  /** Header button → "Move to Lesson…" (U+2026). */
  action: `Move to ${L.singular}…`,
  title: (drill: string) => `Move “${drill || '(untitled)'}”`,
  lede:
    `Pick the ${L.singular} this ${D.singular} belongs in. Its ${QUIZ_TERMS.question.plural}, ` +
    `${QUIZ_TERMS.rehearsal.plural}, ${QUIZ_TERMS.video.plural} and ${QUIZ_TERMS.reading.plural} move with it.`,
  searchPlaceholder: `Search ${L.plural}…`,
  searchLabel: `Search ${L.plural}`,
  listLabel: L.plural,
  current: 'Current',
  hidden: 'Hidden',
  later: 'Later',
  chooseLesson: `Choose a ${L.singular}.`,
  /** Destination line when the pick has drills: prefix + <strong>{lesson}</strong> + '.' */
  addedAfter: (n: number) => `It will be added after the ${drillCount(n)} in `,
  firstIn: `It will be the first ${D.singular} in `,
  nowhereToGo:
    `There’s no other ${L.singular} to move this ${D.singular} to yet. ` +
    `Add one with + ${L.singular} in the tree first.`,
  noResults: (q: string) => `No ${L.plural} match “${q}”.`,
  cancel: 'Cancel',
  confirm: `Move ${D.singular}`,
  busy: 'Moving…',
  errorHead: `Couldn’t move the ${D.singular}.`,
  errorNothingChanged: 'Nothing was changed.',
  errorFallback: 'Check your connection and try again.',
  /** Success strip: "Moved to " + <strong>{lesson}</strong> + suffix. */
  movedTo: 'Moved to ',
  movedSuffix: `. It’s now the last ${D.singular} in that ${L.singular}.`,
  dismiss: 'Dismiss',
} as const;

/**
 * The error detail line (NOTES §2): a 4xx with a string `detail` is shown as-is;
 * anything else (network, 5xx, structured detail) gets the generic fallback.
 * Never a raw `Error: …` / status-code string.
 */
export function moveErrorDetail(e: unknown): string {
  if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
    const detail = (e.body as { detail?: unknown } | null | undefined)?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return MOVE_DRILL_COPY.errorFallback;
}
